import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Autocomplete,
} from '@mui/material';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ClearIcon from "@mui/icons-material/Clear";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import { useHMG } from '../../../HMGContext';
import Http from '../../../services/Http';
import dataService from '../../../services/data.service';
import { handleApiError } from '../../../Utils/errorHandler';

const VIEW_TYPE_OPTIONS = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'DAILY', label: 'Daily' }
];

const UploadDownloadActualsDialog = ({ open, onClose, showToast, disableUploadOption = false, onUploadComplete }) => {
  const [selectedOption, setSelectedOption] = useState(disableUploadOption ? 'download' : 'upload');
  const [selectedYear, setSelectedYear] = useState(dayjs());
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedViewType, setSelectedViewType] = useState(VIEW_TYPE_OPTIONS[0]); // Default: Monthly
  const [uploadFile, setUploadFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const { setCurrentModalName } = useHMG();
  const organizationId = dataService.getSelectedHMGId();
  const propertiesArr = dataService.getPropertiesListForHMG();
  
  // Transform properties to Autocomplete options format
  const propertyOptions = useMemo(() => {
    if (!propertiesArr || propertiesArr.length === 0) return [];
    return propertiesArr.map(property => ({
      value: property.id,
      label: property.name || ''
    }));
  }, [propertiesArr]);

  // Auto-select property when only one option is available
  useEffect(() => {
    if (open && propertyOptions?.length === 1 && !selectedProperty) {
      setSelectedProperty(propertyOptions[0]);
    }
  }, [open, propertyOptions]);

  useEffect(() => {
    if (open && disableUploadOption) {
      setSelectedOption('download');
      setUploadFile(null);
    }
  }, [open, disableUploadOption]);

  const handleOptionChange = (event) => {
    if (disableUploadOption && event.target.value === 'upload') {
      return;
    }
    setSelectedOption(event.target.value);
    setUploadFile(null); // Reset file when switching options
  };

  const handleFileSelect = useCallback((file) => {
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
      ];
      
      // Check file extension first
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const isValidExtension = ['xlsx', 'xls'].includes(fileExtension);
      
      // Check MIME type (some browsers may not set it correctly)
      // Accept if no MIME type is set or if it's in allowed types
      const isValidMimeType = !file.type || file.type === '' || allowedTypes.includes(file.type);
      
      // Accept file if extension is valid OR MIME type is valid (or empty)
      if (!isValidExtension && !isValidMimeType) {
        showToast('Please upload an Excel file (.xlsx, .xls)', 'error');
        return;
      }
      
      setUploadFile(file);
    }
  }, [showToast]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset the input value to allow selecting the same file again
    if (e.target) {
      e.target.value = '';
    }
  }, [handleFileSelect]);

  const handleDialogClose = useCallback(() => {
    setSelectedOption(disableUploadOption ? 'download' : 'upload');
    setSelectedYear(dayjs());
    setSelectedProperty(null);
    setSelectedViewType(VIEW_TYPE_OPTIONS[0]); // Reset to Monthly
    setUploadFile(null);
    setIsDragging(false);
    setCurrentModalName(null);
    if (onClose) onClose();
  }, [onClose, setCurrentModalName, disableUploadOption]);

  const handleUpload = useCallback(async () => {
    if (!uploadFile) {
      showToast('Please select a file to upload', 'error');
      return;
    }

    if (!selectedProperty) {
      showToast('Please select a property', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const propertyId = selectedProperty.value;
      const year = selectedYear.year();
      const url = `actuals-details/upload-actuals?organizationId=${organizationId}&propertyId=${propertyId}&year=${year}`;
      
      const response = await Http.POST(url, formData);
      
      // Handle both 200 (synchronous) and 202 (asynchronous) success responses
      if (response && (response.status === 200 || response.status === 202)) {
        const responseData = response.data || {};
        const message = responseData.message || 'File uploaded successfully';
        const rowCount = responseData.rowCount;
        const responseStatus = responseData.status;

        // Show success message with additional info if available
        if (rowCount) {
          if (responseStatus == "failed") {
            showToast(`${message} (${rowCount} rows)`, 'error');
          }
          else {
            showToast(`${message} (${rowCount} rows)`, 'success');
          }
        } else {
          if (responseStatus == "failed") {
            showToast(message, 'error');
          }
          else {
            showToast(message, 'success');
          }
        }
        if (responseStatus !== 'failed') {
          dataService.invalidateClosedMonthCaches();
        }
        if (typeof onUploadComplete === 'function' && responseStatus !== 'failed') {
          onUploadComplete(uploadFile);
        }
        handleDialogClose();
      } else {
        showToast('Error uploading file. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      handleApiError(error, showToast, 'Error uploading file. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [uploadFile, selectedYear, selectedProperty, showToast, handleDialogClose, organizationId, onUploadComplete]);

  const handleDownload = useCallback(async () => {
    if (!selectedProperty) {
      showToast('Please select a property', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      const year = selectedYear.year();
      const propertyId = selectedProperty.value;
      const viewType = selectedViewType?.value || 'MONTHLY';

      // Updated endpoint: /actuals-details/export-actuals/{organizationId}?year={year}&propertyId={propertyId}&viewType={viewType}
      const url = `actuals-details/export-actuals/${organizationId}?year=${year}&propertyId=${propertyId}&viewType=${viewType}`;
      
      // Use Http.GET with responseType config for file download
      const response = await Http.GET(url, {}, { responseType: 'arraybuffer' });

      if (response.status === 200) {
        // Validate content type
        const contentType = response.headers["content-type"];
        if (contentType !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
          console.error("Invalid content type received:", contentType);
          showToast('Invalid file format received', 'error');
          setIsProcessing(false);
          return;
        }

        // Create a Blob from the binary data
        const blob = new Blob([response.data], { type: contentType });

        // Generate download link
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const filenameSuffix = viewType === 'DAILY' ? 'daily' : 'monthly';
        link.setAttribute("download", `actuals-${filenameSuffix}-${year}.xlsx`);
        document.body.appendChild(link);

        // Trigger the download
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        
        showToast('File downloaded successfully', 'success');
        handleDialogClose();
      } else {
        showToast('Error downloading file. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Download error:', error);
      // Handle arraybuffer error responses specially
      if (error.response?.data instanceof ArrayBuffer) {
        try {
          const text = new TextDecoder().decode(error.response.data);
          const parsedError = JSON.parse(text);
          const errorMessage = parsedError?.message || parsedError?.error || "Error downloading file. Please try again.";
          showToast(errorMessage, "error");
        } catch (parseError) {
          handleApiError(error, showToast, "Error downloading file. Please try again.");
        }
      } else {
        handleApiError(error, showToast, "Error downloading file. Please try again.");
      }
    } finally {
      setIsProcessing(false);
    }
  }, [selectedYear, selectedProperty, selectedViewType, showToast, handleDialogClose, organizationId]);

  const handleAction = useCallback(() => {
    if (selectedOption === 'upload') {
      handleUpload();
    } else {
      handleDownload();
    }
  }, [selectedOption, handleUpload, handleDownload]);

  return (
    <Dialog 
        open={open} 
        fullWidth 
        maxWidth="sm"
        className="p-4"
      >
        <DialogTitle
          sx={{
            padding: "16px 16px 16px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#212B36",
              }}
            >
              Upload/download actuals
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose}>
            <ClearIcon sx={{ width: "16px", height: "16px" }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ padding: "16px 16px 0 16px" }}>
          {/* Year Picker and Radio Options in a row */}
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 3, 
            marginBottom: 3, 
            marginTop: 1,
            flexWrap: "wrap"
          }}>
            {/* Year Picker */}
            <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Year"
                  views={['year']}
                  value={selectedYear}
                  onChange={(newValue) => setSelectedYear(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        ".MuiInputLabel-root": {
                          fontSize: "14px",
                          backgroundColor: "white",
                          paddingLeft: "4px",
                          paddingRight: "4px"
                        },
                        ".MuiOutlinedInput-root": {
                          fontSize: "14px"
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* Radio Options */}
            <Box sx={{ flex: "1 1 auto" }}>
              <RadioGroup
                value={selectedOption}
                onChange={handleOptionChange}
                row
                sx={{ marginBottom: 0 }}
              >
                <FormControlLabel
                  value="upload"
                  control={<Radio />}
                  label="Upload"
                  disabled={disableUploadOption}
                />
                <FormControlLabel
                  value="download"
                  control={<Radio />}
                  label="Download"
                />
              </RadioGroup>
            </Box>
          </Box>

          {/* View Type Dropdown - Only show when download is selected */}
          {selectedOption === 'download' && (
            <Box sx={{ marginBottom: 3 }}>
              <Autocomplete
                id="view-type-select"
                size="small"
                options={VIEW_TYPE_OPTIONS}
                getOptionLabel={(option) => option.label || ""}
                value={selectedViewType}
                onChange={(event, newValue) => {
                  setSelectedViewType(newValue || VIEW_TYPE_OPTIONS[0]);
                }}
                disableClearable
                popupIcon={<KeyboardArrowDownIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="View Type"
                    required
                    sx={{
                      ".MuiInputLabel-root": {
                        fontSize: "14px",
                        backgroundColor: "white",
                        paddingLeft: "4px",
                        paddingRight: "4px"
                      },
                      ".MuiOutlinedInput-root": {
                        fontSize: "14px"
                      }
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Box>
          )}

          {/* Property Selection Dropdown */}
          <Box sx={{ marginBottom: 3 }}>
            <Autocomplete
              id="property-select"
              size="small"
              options={propertyOptions}
              getOptionLabel={(option) => option.label || ""}
              value={selectedProperty}
              onChange={(event, newValue) => {
                setSelectedProperty(newValue);
              }}
              popupIcon={<KeyboardArrowDownIcon />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Property"
                  required
                  sx={{
                    ".MuiInputLabel-root": {
                      fontSize: "14px",
                      backgroundColor: "white",
                      paddingLeft: "4px",
                      paddingRight: "4px"
                    },
                    ".MuiOutlinedInput-root": {
                      fontSize: "14px"
                    }
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
          </Box>

          {/* File Upload Area - Only show when upload is selected */}
          {selectedOption === 'upload' && (
            <Box
              sx={{
                marginTop: selectedOption === 'upload' ? 2 : 0,
                border: `2px dashed ${isDragging ? '#2A5BA2' : '#919EAB52'}`,
                borderRadius: '8px',
                padding: 3,
                textAlign: 'center',
                backgroundColor: isDragging ? '#EEF2F8' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#2A5BA2',
                  backgroundColor: '#EEF2F8',
                }
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileInputChange}
              />
              <CloudUploadIcon 
                sx={{ 
                  fontSize: 48, 
                  color: '#919EAB', 
                  marginBottom: 1 
                }} 
              />
              <Typography
                variant="body2"
                sx={{
                  color: '#637381',
                  fontSize: '14px',
                  marginBottom: 1,
                }}
              >
                {uploadFile 
                  ? uploadFile.name 
                  : 'Drag and drop your file here, or click to browse'}
              </Typography>
              {!uploadFile && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#919EAB',
                    fontSize: '12px',
                  }}
                >
                  Supported formats: .xlsx, .xls
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions className="flex gap-2 !p-4">
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{ color: "#212B36", borderColor: "#919EAB52" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAction}
            variant="contained"
            className='!ms-0'
            disabled={(selectedOption === 'upload' && !uploadFile) || !selectedProperty || isProcessing}
          >
            {isProcessing 
              ? (selectedOption === 'upload' ? 'Uploading...' : 'Downloading...') 
              : (selectedOption === 'upload' ? 'Upload' : 'Download')}
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default UploadDownloadActualsDialog;

